// A site plan's per-access-point lists read as one counted line per distinct
// finding (#1753). At a 130-unit site every list repeated one sentence once per
// access point, so reading the plan meant scrolling past hundreds of lines that
// said the same thing, and the one line that differed was easy to miss.
//
// **The grouping is the page's and nothing else's.** The plan's lines stay one
// per access point in `/v1`, and a line's acceptance stays one per unit
// (ADR-0254 decision 1b, ADR-0269 decision 5d): a group lists every unit under
// it, and the one confirmation an apply opens names each unit on its own row
// and accepts it there (ADR-0436, #1896). Two lines are grouped only where the key a
// list gives says the same thing about each, so a unit whose line differs is
// never folded into a group: it stays a line of its own.
import type { ReactNode } from "react";

/** One distinct finding and the lines that state it, in the plan's order. */
export type LineGroup<T> = { key: string; items: T[] };

/** The lines grouped by `keyOf`, in the plan's order within each group. Groups
 * are ordered smallest first, so a line that differs from the rest — a group of
 * one — is read before the many that agree; groups of one size keep the order
 * their first line had in the plan. */
export function groupLines<T>(items: readonly T[] | undefined, keyOf: (t: T) => string): LineGroup<T>[] {
  return inFirstOrder(items, keyOf)
    .map((g, i) => ({ g, i }))
    .sort((a, b) => a.g.items.length - b.g.items.length || a.i - b.i)
    .map(({ g }) => g);
}

/** The lines grouped by `keyOf`, groups in the order their first line had in
 * the plan. For a list whose order means something, such as the requests an
 * apply makes in turn. */
export function inFirstOrder<T>(items: readonly T[] | undefined, keyOf: (t: T) => string): LineGroup<T>[] {
  const byKey = new Map<string, T[]>();
  for (const item of items ?? []) {
    const key = keyOf(item);
    const list = byKey.get(key);
    if (list) list.push(item); else byKey.set(key, [item]);
  }
  return [...byKey].map(([key, list]) => ({ key, items: list }));
}

/** "1 access point" or "N access points". */
export function accessPoints(n: number): string {
  return `${n} access ${n === 1 ? "point" : "points"}`;
}

/** The units behind a counted line, one click away. Closed by default: the
 * counted line is the reading, and each unit is still listed under it. */
export function Units({ count, label, children }: { count: number; label?: string; children: ReactNode }) {
  return (
    <details className="small" data-units={count}>
      <summary>{label ?? `The ${accessPoints(count)}`}</summary>
      <ul>{children}</ul>
    </details>
  );
}
